import type { ViewProps } from "./types";
import { StatIcon } from "../parts/icons";
import { formatNumber } from "../parts/format";
import { useCountUp } from "../useCountUp";
import { radiusClass, shadowClass } from "../parts/themeTokens";
import { shade } from "../parts/StatItem";

/**
 * Single huge number for high-impact placements (above-the-fold hero strip,
 * marketing landing pages). Picks the cfg.heroKey from the show set.
 */
export function HeroView({ cfg, items, theme }: ViewProps) {
  const main = items.find((i) => i.k === cfg.heroKey) ?? items[0];
  const others = items.filter((i) => i.k !== main.k);
  const animated = useCountUp(main.value, cfg.animate ? 1300 : 0);
  const radius = radiusClass(cfg.radius);
  const shadow = shadowClass(cfg.shadow, theme === "transparent");
  const borderClass = cfg.border ? "border" : "";
  const align =
    cfg.align === "left" ? "items-start text-left" : cfg.align === "right" ? "items-end text-right" : "items-center text-center";

  return (
    <div className="grid h-full place-items-center p-4 sm:p-6">
      <div
        className={
          "relative w-full overflow-hidden " + radius + " " + borderClass + " " + shadow + " p-6 sm:p-10"
        }
        style={{
          background: "var(--lm-card-bg)",
          borderColor: "var(--lm-card-border)",
        }}
      >
        {/* Brand glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full opacity-[0.10] blur-3xl"
          style={{ background: cfg.accent }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-20 -bottom-20 h-72 w-72 rounded-full opacity-[0.08] blur-3xl"
          style={{ background: shade(cfg.accent, -0.25) }}
        />

        <div className={"relative flex flex-col " + align}>
          <div
            className={
              "grid h-12 w-12 place-items-center rounded-2xl text-white shadow-soft animate-fadeIn"
            }
            style={{
              background: `linear-gradient(135deg, ${cfg.accent} 0%, ${shade(cfg.accent, -0.22)} 100%)`,
            }}
            aria-hidden
          >
            <StatIcon k={main.k} className="h-6 w-6" />
          </div>
          <div
            className="mt-4 tabular-nums font-semibold tracking-tight animate-fadeIn"
            style={{
              color: "var(--lm-fg)",
              fontSize: cfg.size === "lg" ? "clamp(48px,10vw,96px)" : cfg.size === "sm" ? "clamp(36px,7vw,64px)" : "clamp(40px,9vw,80px)",
              lineHeight: 1,
              animationDelay: cfg.animate ? "100ms" : "0ms",
              animationFillMode: "both",
            }}
          >
            {formatNumber(animated, cfg.format, cfg.locale)}
          </div>
          <div
            className="mt-2 font-semibold uppercase tracking-[0.1em] animate-fadeIn"
            style={{
              color: "var(--lm-label)",
              fontSize: cfg.size === "lg" ? 14 : 12,
              animationDelay: cfg.animate ? "180ms" : "0ms",
              animationFillMode: "both",
            }}
          >
            {main.label}
          </div>

          {others.length > 0 && (
            <div
              className={
                "mt-6 flex flex-wrap gap-x-6 gap-y-2 " +
                (cfg.align === "left" ? "justify-start" : cfg.align === "right" ? "justify-end" : "justify-center")
              }
            >
              {others.map((o, i) => (
                <Side key={o.k} k={o.k} value={o.value} label={o.label} accent={cfg.accent} cfg={cfg} delayMs={260 + i * 60} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Side({ k, value, label, accent, cfg, delayMs }: {
  k: ViewProps["items"][number]["k"];
  value: number;
  label: string;
  accent: string;
  cfg: ViewProps["cfg"];
  delayMs: number;
}) {
  const animated = useCountUp(value, cfg.animate ? 1100 : 0);
  return (
    <div
      className="flex items-center gap-2 animate-fadeIn"
      style={{ animationDelay: cfg.animate ? `${delayMs}ms` : "0ms", animationFillMode: "both" }}
    >
      <span
        className="grid h-6 w-6 place-items-center rounded-md text-white"
        style={{ background: `linear-gradient(135deg, ${accent} 0%, ${shade(accent, -0.22)} 100%)` }}
        aria-hidden
      >
        <StatIcon k={k} className="h-3.5 w-3.5" />
      </span>
      <span className="tabular-nums font-semibold text-[18px]" style={{ color: "var(--lm-fg)" }}>
        {formatNumber(animated, cfg.format, cfg.locale)}
      </span>
      <span
        className="text-[11px] font-medium uppercase tracking-[0.06em]"
        style={{ color: "var(--lm-label)" }}
      >
        {label}
      </span>
    </div>
  );
}
